import { useEffect, useRef } from "react";
import { useCopilotContext } from "Pages/Dashboard/Copilot/agent/copilotContext";

// Lets Copilot work the agent form or builder that is open beside it, instead of drafting a
// separate agent in the chat. A page calls useAgentPageCopilot with what it shows (its fields, or a
// summary for the builder) and how to apply an edit; Copilot's edit_agent_page tool sends an
// agent_page_edit block, and AgentPageEditBlock hands it to the page through applyAgentPageEdit.
// Nothing is created or saved: the user reviews the page and clicks Create / Save as usual.

let current = null; // { kind, apply } for the page on screen

/**
 * @param kind     "builder" or "template:<key>" (e.g. "template:lookalike")
 * @param title    the page's name, for Copilot ("Lookalike companies to people")
 * @param describe () => ({ fields?: [{ key, label, type, options?, value, userPicks? }], summary? })
 *                 userPicks marks fields the user chooses themselves (sequence, lists): Copilot leaves them.
 * @param apply    async ({ fields?, instruction? }) => ({ changed: [labels] } | { error })
 */
export const useAgentPageCopilot = ({ kind, title, describe, apply }) => {
  useCopilotContext("agents", () => ({
    viewState: { agentPage: { kind, title, pathname: window.location.pathname, ...(describe() || {}) } }
  }));
  const applyRef = useRef(apply);
  applyRef.current = apply;
  useEffect(() => {
    const me = { kind, apply: edit => applyRef.current(edit) };
    current = me;
    return () => {
      if (current === me) current = null;
    };
  }, [kind]);
};

/** Apply a Copilot edit to the agent page on screen. Resolves { changed } or { error }. */
export const applyAgentPageEdit = async block => {
  if (!current) return { error: "The agent page isn't open any more." };
  if (block.pathname && block.pathname !== window.location.pathname) {
    return { error: "That change was for another page." };
  }
  try {
    return (await current.apply({ fields: block.fields, instruction: block.instruction })) || { changed: [] };
  } catch (e) {
    return { error: "Couldn't apply that on the page." };
  }
};
